// Call plugin.
import * as eventTypes from '../eventTypes'

// Helpers
import { getCallById } from '../selectors'

// Libraries.
import { without } from 'lodash/fp'

/**
 * Helper function to format call events.
 * @method callEventHandler
 * @param  {string} type        The type of event.
 * @param  {Object} action      The action that triggered the event.
 * @param  {Object} [params={}] Any non-consistent information to be included in the event.
 * @return {Object} An event object.
 */
export function callEventHandler (type, action, params = {}) {
  /**
   * Call event format.
   * Call events follow a consistent format, based on the consistent format of
   *    their actions.
   */
  const args = {
    ...params
  }
  // There are cases where there is a callId in the params, in that case don't use the payload's id
  // Example: See call event for SESSION_NEW_TRACK
  if (!args.hasOwnProperty('callId') && action.payload.id) {
    args.callId = action.payload.id
  }

  return {
    type,
    args
  }
}

/**
 * Helper function for converting an action to a "call state change" event.
 * Ensures that all such events have consistent parameters.
 * @method stateChangeHandler
 * @param  {Object} action
 * @param  {Object} params
 * @param  {Object} params.prevState Redux state from before the action was processed by reducers.
 * @param  {Object} params.state     Redux state from after the action was processed by reducers.
 * @return {Object} An event object.
 */
export function stateChangeHandler (action, params) {
  // Get the call state before this action updated state.
  const prevCall = getCallById(params.prevState, action.payload.id)

  return callEventHandler(eventTypes.CALL_STATE_CHANGE, action, {
    error: action.payload.error,
    transition: action.payload.transition,
    previous: {
      state: prevCall.state,
      localHold: prevCall.localHold,
      remoteHold: prevCall.remoteHold
    }
  })
}

/**
 * Helper function for converting an action to a "call operation" event.
 * Ensures that all such events have consistent parameters.
 * @method callOperationHandler
 * @param {Object} action
 * @param {Object} params
 * @param {Object} params.prevState The SDK state before this action updated state.
 * @param {string} params.operation The currently occurring operation.
 * @param {string} params.transition The transition reason for the operation change.
 * @param {string} params.isLocal Flag indicating whether the operation is local or not.
 * @param {Object} [any] Additional data provided depending on the operation.
 * @return {Object} An event object.
 */
export function callOperationHandler (action, params) {
  // Get the call state before this action updated state.
  const prevCall = getCallById(params.prevState, action.payload.id)
  let previous

  if (prevCall) {
    previous = params.isLocal ? prevCall.localOp : prevCall.remoteOp
  }

  return callEventHandler(eventTypes.CALL_OPERATION, action, {
    // operation params
    operation: params.operation,
    transition: params.transition,
    isLocal: params.isLocal,
    // previous operation state data
    previous,
    // error
    error: action.payload.error
  })
}

/**
 * Helper function for converting an action into a "tracks added" event.
 * @method trackAddedHandler
 * @param {Object} action
 * @param {Object} action.payload
 * @param {string} action.payload.id The ID of the Call tracks were added to.
 * @param {Array<string>} action.payload.remoteTracks List of track IDs.
 * @param {Object} params
 * @param {Object} params.prevState Redux state before the action changed it.
 * @return {Object} An event object.
 */
export function trackAddedHandler (action, params) {
  // Get the list of remote tracks previously on the call.
  const prevCall = getCallById(params.prevState, action.payload.id)
  const { remoteTracks: prevRemote } = prevCall

  // Get the list of NEW remote tracks actually added to the Call by the operation.
  // In early media scenarios, there will be a duplicate remote track.
  const { localTracks = [], remoteTracks = [] } = action.payload
  const newRemote = without(prevRemote, remoteTracks)

  const trackIds = [...localTracks, ...newRemote]

  // Don't emit the event if no tracks were actually added.
  // For example, when entering dual hold.
  if (!trackIds || trackIds.length === 0) {
    return undefined
  }

  return {
    type: eventTypes.CALL_TRACKS_ADDED,
    args: {
      callId: action.payload.id,
      trackIds
    }
  }
}

/**
 * Helper function for converting an action into a "tracks removed" event.
 * @method trackRemovedHandler
 * @param {Object} action
 * @param {Object} action.payload
 * @param {string} action.payload.id The ID of the Call tracks were removed from.
 * @param {Array<string>} action.payload.remoteTracks List of track IDs.
 * @return {Object} An event object.
 */
export function trackRemovedHandler (action) {
  const { localTracks = [], remoteTracks = [] } = action.payload
  const trackIds = [...localTracks, ...remoteTracks]

  // Don't emit the event if no tracks were actually removed.
  // For example, when leaving dual hold.
  if (!trackIds || trackIds.length === 0) {
    return undefined
  }

  return {
    type: eventTypes.CALL_TRACKS_REMOVED,
    args: {
      callId: action.payload.id,
      trackIds
    }
  }
}
